import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ShowToast } from "../../components/toast";
import { api } from "../api";
import { getUser } from "../utils";
import type { HighlightsData } from "./use-highlights";

const MAX_PHOTO_SIZE_BYTES = 10 * 1024 * 1024;
// Hotel photos must be JPEG or PNG — the ad / Google Business pipelines only
// accept those. Enforced here for a clear error AND on the backend signed-URL
// endpoint so it can't be bypassed; the file picker's `accept` narrows it too.
const ALLOWED_PHOTO_MIME_TYPES = ["image/jpeg", "image/png"];

function isAllowedPhotoType(file: File): boolean {
  return ALLOWED_PHOTO_MIME_TYPES.includes(file.type.toLowerCase());
}

export type PhotoRejectionReason = "type" | "size";

export interface PhotoPartition {
  valid: File[];
  skipped: { name: string; reason: PhotoRejectionReason }[];
}

/**
 * Split selected files into uploadable photos and rejected ones (with the
 * reason — wrong `type` or too-large `size`). The Highlights editor uses the
 * skipped reasons to show a short inline message; only `valid` files upload.
 */
export function partitionPhotoFiles(files: File[]): PhotoPartition {
  const valid: File[] = [];
  const skipped: { name: string; reason: PhotoRejectionReason }[] = [];
  for (const file of files) {
    if (!isAllowedPhotoType(file)) {
      skipped.push({ name: file.name, reason: "type" });
    } else if (file.size > MAX_PHOTO_SIZE_BYTES) {
      skipped.push({ name: file.name, reason: "size" });
    } else {
      valid.push(file);
    }
  }
  return { valid, skipped };
}

export class HotelPhotoUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "HotelPhotoUploadError";
  }
}

async function uploadOnePhotoToS3(userId: string, file: File): Promise<string> {
  if (!isAllowedPhotoType(file)) {
    throw new HotelPhotoUploadError("Please upload a JPEG or PNG image");
  }
  if (file.size > MAX_PHOTO_SIZE_BYTES) {
    throw new HotelPhotoUploadError("Each photo must be less than 10MB");
  }

  const { uploadUrl, accessUrl } = await api.getSignedUrl(userId, {
    uploadType: "HOTEL_PHOTO",
    mimeType: file.type,
    fileName: file.name,
  });

  const uploadResponse = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  if (!uploadResponse.ok) {
    throw new Error(
      `S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`,
    );
  }

  return accessUrl;
}

/**
 * Uploads one or more hotel photos for the Highlights step's "add more photos
 * for us to use" option. Each file goes to S3 via a signed URL, then the
 * resulting public URLs are persisted on the user (for the team to use in
 * Google Business Profile / ads). On success the ["highlights", userId] cache
 * is updated with the merged photo list returned by the backend.
 */
export function useHotelPhotoUpload(userId?: string) {
  const queryClient = useQueryClient();

  const uploadMutation = useMutation<string[], Error, FileList | File[]>({
    mutationFn: async (files) => {
      const id = userId ?? getUser()?.id;
      if (!id) {
        throw new HotelPhotoUploadError(
          "You must be signed in to upload photos",
        );
      }

      const fileArray = Array.from(files);
      if (fileArray.length === 0) {
        return [];
      }

      // Callers pre-filter with `partitionPhotoFiles` and pass only valid
      // photos; `uploadOnePhotoToS3` re-checks type/size as a last-resort guard.
      const accessUrls: string[] = [];
      for (const file of fileArray) {
        accessUrls.push(await uploadOnePhotoToS3(id, file));
      }

      const { userProvidedPhotos } = await api.addHotelPhotos(id, accessUrls);
      return userProvidedPhotos;
    },
    onSuccess: (userProvidedPhotos) => {
      if (userId) {
        queryClient.setQueryData<HighlightsData>(
          ["highlights", userId],
          (prev) => (prev ? { ...prev, userProvidedPhotos } : prev),
        );
      }
      ShowToast({ type: "success", message: "Photos uploaded" });
    },
    onError: (error) => {
      const message =
        error instanceof HotelPhotoUploadError
          ? error.message
          : "Failed to upload photos. Please try again.";
      ShowToast({ type: "error", message });
    },
  });

  return {
    uploadPhotos: uploadMutation.mutateAsync,
    isUploading: uploadMutation.isLoading,
  };
}
